import React, { useState, useEffect } from 'react';
import { getCookieConsent, updateCookieConsent } from '../../utils/cookieConsent';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';

const CookieSettings = () => {
  const [currentConsent, setCurrentConsent] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    const consent = getCookieConsent() || 'none';
    setCurrentConsent(consent);
    
    // Set individual preferences based on consent
    if (consent === 'all') {
      setCookiePreferences({
        analytics: true,
        marketing: true,
        preferences: true
      });
    } else if (consent === 'necessary') {
      setCookiePreferences({
        analytics: false,
        marketing: false,
        preferences: false
      });
    }
  }, []);

  const handleIndividualChange = (category, value) => {
    const newPreferences = {
      ...cookiePreferences,
      [category]: value
    };
    
    setCookiePreferences(newPreferences);
    
    // Determine consent type based on preferences
    let consentType = 'necessary'; // Default to necessary only
    if (newPreferences.analytics && newPreferences.marketing && newPreferences.preferences) {
      consentType = 'all';
    } else if (!newPreferences.analytics && !newPreferences.marketing && !newPreferences.preferences) {
      consentType = 'rejected';
    } else {
      // Custom consent - store individual preferences
      localStorage.setItem('cookiePreferences', JSON.stringify(newPreferences));
      consentType = 'custom';
    }
    
    updateCookieConsent(consentType);
    setCurrentConsent(consentType);
    setShowSuccess(true);
    
    // Hide success message after 3 seconds
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleConsentChange = (consentType) => {
    updateCookieConsent(consentType);
    setCurrentConsent(consentType);
    
    // Update individual preferences based on consent type
    if (consentType === 'all') {
      setCookiePreferences({
        analytics: true,
        marketing: true,
        preferences: true
      });
    } else {
      setCookiePreferences({
        analytics: false,
        marketing: false,
        preferences: false
      });
    }
    
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body p-5">
              <h2 className="mb-4">Cookie Settings</h2>
              
              {showSuccess && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  Your cookie preferences have been updated successfully!
                  <button type="button" className="btn-close" onClick={() => setShowSuccess(false)}></button>
                </div>
              )}

              <div className="mb-4">
                <p className="text-muted">
                  Manage your cookie preferences. You can change these settings at any time.
                </p>
              </div>

              <div className="cookie-categories mb-4">
                <div className="cookie-category mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Essential Cookies</h6>
                    <span className="badge bg-success">Always Active</span>
                  </div>
                  <p className="text-muted small mb-0">
                    These cookies are necessary for the website to function properly. 
                    They enable basic functions like page navigation and access to secure areas.
                  </p>
                </div>

                <div className="cookie-category mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Analytics Cookies</h6>
                    <div className="form-check form-switch">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="analyticsCookies"
                        checked={cookiePreferences.analytics}
                        onChange={(e) => handleIndividualChange('analytics', e.target.checked)}
                      />
                    </div>
                  </div>
                  <p className="text-muted small mb-0">
                    These cookies help us understand how visitors interact with our website 
                    by collecting and reporting information anonymously.
                  </p>
                </div>

                <div className="cookie-category mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Marketing Cookies</h6>
                    <div className="form-check form-switch">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="marketingCookies"
                        checked={cookiePreferences.marketing}
                        onChange={(e) => handleIndividualChange('marketing', e.target.checked)}
                      />
                    </div>
                  </div>
                  <p className="text-muted small mb-0">
                    These cookies are used to track visitors across websites to display 
                    relevant and engaging advertisements.
                  </p>
                </div>

                <div className="cookie-category">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Preference Cookies</h6>
                    <div className="form-check form-switch">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="preferenceCookies"
                        checked={cookiePreferences.preferences}
                        onChange={(e) => handleIndividualChange('preferences', e.target.checked)}
                      />
                    </div>
                  </div>
                  <p className="text-muted small mb-0">
                    These cookies allow the website to remember choices you make and 
                    provide enhanced, more personal features.
                  </p>
                </div>
              </div>

              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end">
                <SecondaryButton
                  onClick={() => handleConsentChange('necessary')}
                >
                  Accept Necessary Only
                </SecondaryButton>
                <PrimaryButton
                  onClick={() => handleConsentChange('all')}
                >
                  Accept All
                </PrimaryButton>
              </div>

              <div className="mt-4 pt-4 border-top">
                <h6>Current Status</h6>
                <p className="text-muted small mb-0">
                  Your current cookie consent status: <strong>
                    {currentConsent === 'all' ? 'All cookies accepted' : 
                     currentConsent === 'necessary' ? 'Only necessary cookies' : 
                     currentConsent === 'custom' ? 'Custom preferences' : 
                     'No choice made yet'}
                  </strong>
                </p>
                {currentConsent === 'custom' && (
                  <div className="mt-2">
                    <p className="text-muted small mb-1">Active cookies:</p>
                    <ul className="text-muted small mb-0">
                      {cookiePreferences.analytics && <li>Analytics</li>}
                      {cookiePreferences.marketing && <li>Marketing</li>}
                      {cookiePreferences.preferences && <li>Preferences</li>}
                      {!cookiePreferences.analytics && !cookiePreferences.marketing && !cookiePreferences.preferences && 
                        <li>None (only essential cookies)</li>}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieSettings; 